import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import jwtDecode from "jwt-decode";

import { config } from "@/messenger/config";
import { setPromptsWereShown } from "@/messenger/ducks/general/prompts.duck";
import { useMessengerAuthorize } from "@/messenger/hooks/useMessengerAuthorize";
import { useAuth } from "@/messenger/providers/AuthProvider";
import { localStorageKeys } from "@/messenger/app.settings";

export function useFetchUserMetadata() {
    const authClient = useAuth();
    const [userMetadata, setUserMetadata] = useState(null);
    const [accessToken, setAccessToken] = useState(null);
    const [metadataUrl, setMetadataUrl] = useState(null);
    const dispatch = useDispatch();
    const authCredentials = useMessengerAuthorize(authClient);

    useEffect(() => {
        const token = localStorage.getItem(localStorageKeys.accessToken);
        const metadataUrlFromLocalStrorage = localStorage.getItem(localStorageKeys.metadataUrl);
        const userMetadataFromLocalStorage = localStorage.getItem(localStorageKeys.userMetadata);

        if (metadataUrlFromLocalStrorage) {
            setMetadataUrl(metadataUrlFromLocalStrorage);
        }

        if (userMetadataFromLocalStorage) {
            setUserMetadata(JSON.parse(userMetadataFromLocalStorage));
        }

        if (token) {
            const decodedToken = jwtDecode(token);
            if (decodedToken) {
                const tokenExpiry = decodedToken['exp'];
                const timeLeft = tokenExpiry - Math.round(Date.now() / 1000);
                if ((timeLeft - 1000) > 0) {
                    setAccessToken(token);
                } else {
                    setAccessToken(null)
                }
            } else {
                setAccessToken(null)
            }
        }
    }, []);

    function getUserMetadata() {
        const options: RequestInit = {
            headers: { Authorization: `Bearer ${authCredentials.token}` },
        };
        let tokenUrl = config.api.messenger.rest().origin;
        tokenUrl = "https://api-ui.messagingkit.com";

        return fetch(`${tokenUrl}/auth0/token`, options)
            .then(tokenBlob => tokenBlob.json())
            .then(({ token, apiUrl }) => {
                localStorage.setItem(localStorageKeys.accessToken, token);
                localStorage.setItem(localStorageKeys.metadataUrl, apiUrl);
                setAccessToken(token);
                setMetadataUrl(apiUrl);
                // return fetch(`${apiUrl}users/${authClient.user.sub}`, {
                return fetch(`https://api-ui.messagingkit.com/userinfo/${authClient?.user?.id}`, {
                    method: "GET",
                    headers: {
                        'Authorization': `Bearer ${authClient?.token}`,
                    }
                })
                    .then(metadataBlob => metadataBlob.json())
                    .then(metadata => {
                        localStorage.setItem(localStorageKeys.userMetadata, JSON.stringify({ ...metadata?.user?.data }));
                        setUserMetadata({ ...metadata?.user?.data })
                    })
                    .catch(e => {
                        // TODO: use Sentry (or similar) to log errors
                        console.log(e);
                    });
            });
    }

    useEffect(() => {
        if ((!accessToken || !userMetadata || !metadataUrl) && authCredentials) {
            getUserMetadata();
        }
    }, [authCredentials, accessToken, userMetadata, metadataUrl]);

    useEffect(() => {
        if (userMetadata?.werePromptsShown) {
            dispatch(setPromptsWereShown);
        }
    }, [userMetadata]);

    return [accessToken, userMetadata, setUserMetadata, metadataUrl];
}